import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { servicesApi } from '../services/api';

interface ServiceDetail {
  id: number;
  slug: string;
  name_zh: string;
  name_vi: string;
  description_zh: string;
  description_vi: string;
  details_zh: string;
  details_vi: string;
  image_url: string;
  icon_class: string;
}

const iconMap: Record<string, string> = {
  bolt: '⚡',
  thermostat: '🌡️',
  wind: '💨',
  building: '🏗️',
  layers: '🧱'
};

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const [service, setService] = useState<ServiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await servicesApi.getById(Number(id));
        if (res.success && res.data) {
          setService(res.data);
        } else {
          setError('服务不存在');
        }
      } catch (err) {
        setError('获取服务详情失败');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  const parseDetails = (detailsJson: string): string[] => {
    try {
      return JSON.parse(detailsJson || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{language === 'zh' ? '加载中...' : 'Đang tải...'}</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {language === 'zh' ? '服务未找到' : 'Không tìm thấy dịch vụ'}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/services"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {language === 'zh' ? '← 返回服务列表' : '← Quay lại danh sách dịch vụ'}
          </Link>
        </div>
      </div>
    );
  }

  const icon = iconMap[service.icon_class] || '🔧';
  const details = parseDetails(language === 'zh' ? service.details_zh : service.details_vi);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <Link to="/services" className="text-blue-200 hover:text-white mb-6 inline-block transition-colors">
            ← {language === 'zh' ? '返回业务范围' : 'Quay lại dịch vụ'}
          </Link>
          <div className="flex items-center space-x-4 mb-6">
            <div className="text-5xl">{icon}</div>
            <h1 className="text-4xl md:text-5xl font-bold">
              {language === 'zh' ? service.name_zh : service.name_vi}
            </h1>
          </div>
          <p className="text-xl text-blue-100 leading-relaxed max-w-4xl">
            {language === 'zh' ? service.description_zh : service.description_vi}
          </p>
        </div>
      </section>

      {/* 服务详情 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              {language === 'zh' ? '服务详情' : 'Chi tiết dịch vụ'}
            </h2>

            {service.image_url && (
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center">
                <img
                  src={service.image_url}
                  alt={service.name_zh}
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {details.length > 0 ? details.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed pt-1">{detail}</p>
                  </div>
                )) : (
                  <p className="text-gray-500 col-span-2 text-center py-8">
                    {language === 'zh' ? '暂无详细内容' : 'Chưa có nội dung chi tiết'}
                  </p>
                )}
              </div>
            </div>

            {/* 底部返回导航 */}
            <div className="mt-12 text-center">
              <Link
                to="/services"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                ← {language === 'zh' ? '返回业务范围' : 'Quay lại dịch vụ'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
